import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { CalendarClock, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
};

const TYPE_STYLE = {
  tour: 'bg-emerald-100 text-emerald-700',
  walkthrough: 'bg-emerald-100 text-emerald-700',
  meeting: 'bg-indigo-100 text-indigo-700',
  consultation: 'bg-indigo-100 text-indigo-700',
  sign_up: 'bg-teal-100 text-teal-700',
  callback: 'bg-amber-100 text-amber-700',
};

/**
 * "Booked Appointments" card at the top of the Individual tab.
 * Reuses the same transcript-extracted /analytics/upcoming-appointments
 * data the Executive tab uses, scoped to ONE agent:
 *  - agents hit the endpoint with no agent_id (server self-scopes by RBAC)
 *  - managers viewing a rep pass agent_id=<selected rep>
 */
const MyAppointmentsCard = ({ domainId, agentId, isSelf }) => {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    p.set('days', '7');
    if (agentId) p.set('agent_id', agentId);
    axios.get(`${API}/analytics/upcoming-appointments?${p}`, { timeout: 20000 })
      .then((r) => setData(r.data))
      .catch((e) => { if (e?.response?.status === 403) setHidden(true); });
  }, [domainId, agentId]);

  if (hidden || !data) return null;
  const rows = (data.agents || [])
    .flatMap((a) => a.appointments || [])
    .sort((x, y) => (x.when || '').localeCompare(y.when || ''))
    .slice(0, 6);

  return (
    <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white shadow-sm" data-testid="my-appointments-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2 font-bold text-emerald-900">
            <CalendarClock className="w-4 h-4 text-emerald-600" />
            {isSelf ? 'Your Booked Appointments' : 'Booked Appointments'}
          </span>
          <span className="text-2xl font-bold text-emerald-700" data-testid="my-appointments-count">{data.total ?? 0}</span>
        </CardTitle>
        <p className="text-xs text-slate-400">
          Next 7 days — tours, walk-throughs, meetings & callbacks extracted from {isSelf ? 'your' : 'their'} call transcripts.
        </p>
      </CardHeader>
      <CardContent className="space-y-1.5 pt-0">
        {rows.length === 0 && (
          <p className="text-sm text-slate-400 italic" data-testid="my-appointments-empty">
            No booked appointments in the next 7 days.
          </p>
        )}
        {rows.map((r, i) => (
          <div key={i} className="border border-emerald-100 bg-white rounded-lg" data-testid={`my-appointment-row-${i}`}>
            <button
              className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left"
              onClick={() => setOpen(open === i ? null : i)}
              data-testid={`my-appointment-toggle-${i}`}
            >
              <span className="text-xs flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-700">{fmtWhen(r.when)}</span>
                {r.type && (
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${TYPE_STYLE[r.type] || 'bg-slate-100 text-slate-600'}`}>
                    {String(r.type).replace('_', ' ')}
                  </span>
                )}
                {r.customer_phone && <span className="font-mono text-slate-500">{r.customer_phone}</span>}
              </span>
              {open === i ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
            </button>
            {open === i && (
              <div className="px-3 pb-2 text-xs text-slate-500" data-testid={`my-appointment-snippet-${i}`}>
                <p className="italic border-l-2 border-emerald-300 pl-2">"{r.snippet || 'No snippet stored'}"</p>
                <p className="text-[10px] text-slate-400 mt-1">Said as: "{r.when_text}" · confidence {Math.round((r.confidence || 0) * 100)}%</p>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default MyAppointmentsCard;
